import { useContext, useEffect, useState } from "react";
import { ChartContext } from "../../../Context/ChartContext/ChartContext";
import MainContext from "../../../Context/MainContext/MainContext";
import MUIGrid from "@mui/material/Grid";
import MUICheckbox from "@mui/material/Checkbox";
import MUIFormControlLabel from "@mui/material/FormControlLabel";
import MUITextField from "@mui/material/TextField";
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUIFormControl from "@mui/material/FormControl";
import MUIFormHelperText from "@mui/material/FormHelperText";
import MUITextSnippetIcon from "@mui/icons-material/TextSnippet";

import Card from "../../Card";
import OrderActivity from "../../../Utility/Crud/OrderActivity";
import Patient from "../../../Utility/Crud/Patient";

/**
 * CardChartCreateTicket renders a Card component
 * displaying patient demographic information.
 * @returns {JSX.Element}
 */
const className = "CardChartCreateTicketNote";
const title = "Note";
const icon = MUITextSnippetIcon;

export default function CardChartCreateTicketNote(props) {
  const chartContext = useContext(ChartContext);
  const mainContext = useContext(MainContext);
  const className = "CardChartCreateTicketNote";
  const [confirm, setConfirm] = useState(false);
  const [products, setProducts] = useState([]);
  const [newNoteId, setNewNoteId] = useState(null);
  const [newProductId, setNewProductId] = useState(null);
  const parts = location.pathname.split("/");

  const order = chartContext.chart?.orderActivity[parts[4]] || null;
  const patient = chartContext.chart?.patient[chartContext.patientId] || null;
  /**
   * Gets the patient's products for the order.
   */
  useEffect(() => {
    if (chartContext.pageMode === "pickup") {
      const products = OrderActivity.getProducts(order, chartContext.chart).map(
        (product, index) => ({
          orderId: order.id,
          points: 0, // Always 0 for pickups
          label: OrderActivity.getProductDisplayName(
            product,
            mainContext.baseUnitsIndexed,
            mainContext.vendorsIndexed,
            mainContext.productSubTypesIndexed,
          ),
          value: product.id,
          detail: product,
          key: index,
        }),
      );
      setProducts(products);
    }
  }, []);

  // Get a new negative id for the note only once when the component mounts
  useEffect(() => {
    if (!(chartContext.orderId < 0)) {
      return;
    }
    const newProductId = chartContext.getNegativeId();
    const newNoteId = chartContext.getNegativeId();
    setNewProductId(newProductId);
    setNewNoteId(newNoteId);
    chartContext.updateValidationRules({
      key: className,
      rules: [
        {
          target: "note",
          id: newNoteId,
          field: "text",
          rule: {
            required: true,
          },
        },
        {
          target: "orderActivityProducts",
          id: newProductId,
          field: "productsId",
          rule: {
            required: chartContext.pageMode === "pickup" ? true : false,
          },
        },
      ],
    });

    const note = {
      [newNoteId]: {
        id: newNoteId,
        accountId: OrderActivity.getAccount(order, chartContext.chart)?.id,
        activityId: chartContext.orderId,
        assignedToId: null,
        completed: true,
        createdById: 1, // TODO Michael update note creation to not require this and throw it away if provided
        createdAt: new Date().toISOString(), //Is this correct?
        dueDate: null,
        isAutoVerification: false,
        patientName: Patient.getDisplayName(patient),
        task: false,
        text: "",
        type: "ActivityNote",
      },
    };
    const existingOrderProducts =
      chartContext.chart.orderActivityProducts || {};

    if (chartContext.pageMode === "pickup") {
      const orderActivityProducts = {
        ...existingOrderProducts,
        [newProductId]: {
          orderActivityId: chartContext.orderId,
          productsId: null,
        },
      };
      chartContext.onChange({
        orderActivityProducts: orderActivityProducts,
      });
    }

    chartContext.onChange({
      note: note,
    });
  }, []);

  /**
   * Determines which view to render based on edit mode state.
   * @returns {JSX.Element} Either edit view or read-only view
   */
  const renderContent = () => {
    const handleAddProduct = (product) => {
      const existingProducts = chartContext.chart.product || {};
      const products = {
        ...existingProducts,
        [newProductId]: {
          id: newProductId,
          abnRequired: false,
          approved: false,
          hcpcs:
            mainContext.productSubTypesIndexed[product.detail.productSubTypeId]
              ?.hcpcs || "",
          productSubTypeId: product.detail.productSubTypeId,
          productTypeId: product.detail.productTypeId,
          deliverable: true,
          billable: false, // Always false for Pickups
          qty: 1,
          points: 0, // Always 0 for Pickups
          serial: product.detail?.serial || "",
          amount: 0,
          isRental: false,
          upgradedFromProductId: null,
        },
      };

      chartContext.onChange({
        product: products,
        orderActivityProducts: {
          [newProductId]: { productsId: newProductId },
        },
      });
    };

    return (
      <MUIGrid container spacing={8} rowSpacing={2}>
        <MUIGrid item xs={12}>
          <MUITextField
            multiline
            rows={4}
            fullWidth={true}
            InputLabelProps={{ shrink: true }}
            inputProps={{ style: { resize: "both" } }}
            label={"Note"}
            onChange={(e) => {
              chartContext.onChange({
                note: { [newNoteId]: { text: e.target.value } },
              });
            }}
            variant="standard"
            required={true}
            sx={{ mb: 2 }}
            error={
              chartContext.validationIssues[className]?.note?.[newNoteId]?.text
                ? true
                : false
            }
            helperText={
              chartContext.validationIssues[className]?.note?.[newNoteId]?.text
                ? chartContext.validationIssues[className]?.note?.[newNoteId]
                    ?.text
                : ""
            }
          />
        </MUIGrid>
        {chartContext.pageMode === "pickup" && (
          <MUIGrid item lg={6} xs={12}>
            <MUIAutocomplete
              options={products || []}
              isOptionEqualToValue={(option, value) =>
                option?.value === value?.value
              }
              getOptionLabel={(option) => option?.label}
              onChange={(e, newValue) => {
                if (newValue) {
                  handleAddProduct(newValue);
                } else {
                  chartContext.onChange({
                    orderActivityProducts: {
                      [newProductId]: { productsId: null },
                    },
                  });
                }
              }}
              getOptionKey={(option) => option.key}
              renderInput={(params) => (
                <MUITextField
                  {...params}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  variant="standard"
                  label="Product"
                  fullWidth
                  required={true}
                  error={
                    chartContext.validationIssues[className]
                      ?.orderActivityProducts?.[newProductId]?.productsId
                      ? true
                      : false
                  }
                  helperText={
                    chartContext.validationIssues[className]
                      ?.orderActivityProducts?.[newProductId]?.productsId
                      ? chartContext.validationIssues[className]
                          ?.orderActivityProducts?.[newProductId]?.productsId
                      : ""
                  }
                />
              )}
            />
          </MUIGrid>
        )}
        <MUIGrid item xs={12}>
          <MUIFormControl
            component="fieldset"
            required={true}
            error={
              chartContext.createTicketValidationIssues.confirm ? true : false
            }
          >
            <MUIFormControlLabel
              control={
                <MUICheckbox
                  checked={confirm}
                  onChange={() => {
                    setConfirm(!confirm);
                    chartContext.setCreateTicketValidationRules({
                      ...chartContext.createTicketValidationRules,
                      confirm: !confirm,
                    });
                  }}
                  size="small"
                  required={true}
                />
              }
              label="I confirm that all information has been reviewed and verified as accurate."
            />
            {chartContext.createTicketValidationIssues.confirm ? (
              <MUIFormHelperText sx={{ ml: 0 }}>
                {chartContext.createTicketValidationIssues.confirm
                  ? chartContext.createTicketValidationIssues.confirm
                  : ""}
              </MUIFormHelperText>
            ) : null}
          </MUIFormControl>
        </MUIGrid>
      </MUIGrid>
    );
  };

  return <Card renderContent={renderContent} getTitle={() => title} />;
}

CardChartCreateTicketNote.meta = {
  className: className,
  title: title,
  icon: icon,
};
